
// Module-level guard so flex-form.js is injected exactly once per page lifetime.
let scriptLoaded = null

function loadFlexFormScript(src) {
  if (scriptLoaded) return scriptLoaded
  scriptLoaded = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', reject)
      if (window.Splitit && window.Splitit.FlexForm) resolve()
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => resolve()
    script.onerror = err => {
      scriptLoaded = null
      reject(err)
    }
    document.head.appendChild(script)
  })
  return scriptLoaded
}

export default {
  name: 'SplititHostedFields',
  props: {
    order: { required: true, type: Object },
    currency: { required: true, type: String },
    numberOfInstallments: { required: true, type: Number },
    customer: { required: true, type: Object },
    billingAddress: { required: true, type: Object },
    shippingAddress: { required: true, type: Object }
  },
  data() {
    return {
      loading: false,
      ipn: null,
      stickyOrderId: null,
      amount: null,
      hostedFields: null,
      initiatedOrderDetailsSnapshot: null
    }
  },
  watch: {
    // Splitit's iframe maintains its own state. For billing/consumer edits we
    // call updateDetails — never re-run setup, which would tear down the iframe.
    billingAddress: {
      deep: true,
      handler(next) {
        if (this.hostedFields) {
          try {
            this.hostedFields.updateDetails({ billingAddress: next })
          } catch (err) {
            this.$emit('error', err)
          }
        }
      }
    },
    customer: {
      deep: true,
      handler(next) {
        if (this.hostedFields) {
          try {
            this.hostedFields.updateDetails({ consumer: next })
          } catch (err) {
            this.$emit('error', err)
          }
        }
      }
    }
  },
  async mounted() {
    await this.bootstrap()
  },
  beforeDestroy() {
    this.teardown()
  },
  methods: {
    async bootstrap() {
      this.loading = true
      try {
        const url = this.$config.splititFlexFormUrl
        if (!url) {
          throw new Error('SPLITIT_FLEX_FORM_URL is not configured')
        }
        await loadFlexFormScript(url)

        const {
          ipn,
          stickyOrderId,
          amount
        } = await this.$salesApi.initiatePlan(
          {
            ...this.order,
            numberOfInstallments: this.numberOfInstallments,
            currency: this.currency
          },
          this.$store.getters['split-testing/splitTestFunnel']
        )
        this.ipn = ipn
        this.stickyOrderId = stickyOrderId
        this.amount = amount

        this.hostedFields = window.Splitit.FlexForm.setup({
          ipn,
          container: 'splitit-payment-form',
          consumer: this.customer,
          billingAddress: this.billingAddress,
          paymentButton: { isCustom: true },
          onSuccess: ({ ipn: paidIpn } = {}) => {
            this.$emit('success', paidIpn || this.ipn)
          },
          onError: err => {
            this.$emit('error', err)
          }
        })
        if (typeof this.hostedFields.show === 'function') {
          this.hostedFields.show()
        }
        this.initiatedOrderDetailsSnapshot = this.orderDetailsSnapshot()
        this.$emit('initiated', { ipn, stickyOrderId, amount })
      } catch (err) {
        this.$emit('error', err)
      } finally {
        this.loading = false
      }
    },
    teardown() {
      if (this.hostedFields) {
        try {
          this.hostedFields.destroy()
        } catch (err) {
          if (this.$bugsnag) this.$bugsnag.notify(err)
        }
        this.hostedFields = null
      }
    },
    orderDetailsSnapshot() {
      // promoCode and quantity feed the pending order amount, so a change
      // to either between initiate and submit makes it stale.
      const order = this.order || {}
      return JSON.stringify({
        customer: this.customer,
        billingAddress: this.billingAddress,
        shippingAddress: this.shippingAddress,
        promoCode: order.promoCode,
        quantity: order.quantity
      })
    },
    // Returns true if a reinit happened (caller must re-collect the card).
    // The pending order amount is fixed at initiate, so any customer/address
    // change needs a fresh /splitit/initiate to stay aligned.
    async reinitIfOrderDetailsChanged() {
      if (this.orderDetailsSnapshot() === this.initiatedOrderDetailsSnapshot) {
        return false
      }
      this.teardown()
      await this.bootstrap()
      return true
    },
    // Returns true if a charge was attempted (caller keeps busy until
    // onSuccess/onError), false if validation bailed (caller resets busy).
    async pay() {
      if (!this.hostedFields) {
        this.$emit('error', new Error('Splitit hosted fields not initialized'))
        return false
      }
      const valid = await Promise.resolve(this.hostedFields.isValid())
      if (!valid) {
        this.hostedFields.triggerValidation()
        return false
      }
      try {
        await this.hostedFields.pay()
        // FlexForm's onSuccess handler emits 'success' with the IPN.
        return true
      } catch (err) {
        this.$emit('error', err)
        return true
      }
    }
  }
}
